import React, {
	createContext,
	type ReactNode,
	Suspense,
	useCallback,
	useContext,
	useEffect,
	useMemo,
} from 'react';

import { GlobalSideNavigation } from '@atlassian/global-side-navigation/GlobalSideNavigation';
import { GlobalSideNavigationAGGRelayConnection } from '@atlassian/global-side-navigation/GlobalSideNavigationAGGRelayConnection';
import type {
	ShortcutsConfig,
	ShortcutsSlotProps,
} from '@atlassian/global-side-navigation/common/types';
import type { GlobalSideNavigationAGGRelayConnectionQuery$data } from '@atlassian/global-side-navigation/GlobalSideNavigationAGGRelayConnectionQuery.graphql';
import { InvitedAppShortcutsContainer } from '@atlassian/cross-join-nudges/invited-app-shortcuts/invited-app-shortcuts-container/main';
import { useInvitedAppProductLinks } from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/invited-app-product-links';
import type { ShortcutItemsConfig } from '@atlassian/cross-join-nudges/types';
import type { GlobalShortcutLinkItem } from '@atlassian/global-shortcuts/types';
import { useJira } from '@atlassian/global-shortcuts/jira';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { expVal } from '@confluence/feature-experiments';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { getCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';

import { useNetworkStorageParams } from './hooks/useNetworkStorageParams.ts';
import { useGlobalAppShortcuts } from './hooks/useGlobalAppShortcuts.ts';
import { useForYouConfig } from './hooks/useForYouConfig.tsx';
import { useMenuItemsConfig } from './hooks/useMenuItemsConfig.ts';
import { useResolveInvitedAppShortcuts } from './AppShortcuts/hooks/useResolveInvitedAppShortcuts.tsx';
import { CustomHomeItem } from './components/CustomHomeItem.tsx';
import { useShortcutVisibilityOverrides } from './AppShortcuts/hooks/useShortcutVisibilityOverrides.tsx';

const defaultMenuVisibility = [
	{ menuId: 'drafts', visible: false },
	{ menuId: 'tasks', visible: false },
	{ menuId: 'templates', visible: false },
];

// GlobalSideNavigation persists its resized width to network storage only. The Suspense fallback
// skeleton (SideNavigationSkeleton in @confluence/page-layout) sizes itself from the legacy
// localStorage key PERSISTED_LEFT_SIDEBAR_WIDTH, so without this mirror the skeleton renders at the
// stale/default width and visibly snaps to the real width once the nav loads. Mirroring the
// committed width keeps the skeleton and the loaded nav in sync.
const mirrorSideNavWidthToLocalStorage = ({ finalWidth }: { finalWidth: number }) => {
	confluenceLocalStorageInstance.setItem(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_WIDTH,
		finalWidth,
	);
};

const hasGlobalAppShortcutsAccess = (accessStatus: AccessStatus | null) =>
	![
		AccessStatus.ANONYMOUS_ACCESS,
		AccessStatus.EXTERNAL_SHARE_ACCESS,
		AccessStatus.EXTERNAL_COLLABORATOR_ACCESS,
		AccessStatus.NOT_PERMITTED,
		null,
	].includes(accessStatus);

// Carries the resolved invited-app-shortcuts result to the ShortcutsSlot component (below), which
// GSN renders deep inside its own tree. Context (rather than a closure) lets the slot component
// live at module scope with a stable identity, so GSN never remounts the shortcuts subtree when the
// resolver result changes reference.
const InvitedAppShortcutsResolverContext = createContext<ReturnType<
	typeof useResolveInvitedAppShortcuts
> | null>(null);

/**
 * `ShortcutsSlot` implementation (NAVX-5236) for the invited-app-shortcuts experiment cohort.
 *
 * GSN hands us the already-resolved `shortcutItems` (visibility-filtered, ordered, and
 * analytics-wrapped) plus a pre-bound `Component`. We feed those items to
 * `InvitedAppShortcutsContainer` as its `shortcutsConfig` so it can decorate the eligible
 * invited-app entries in place (hover popup ref, activation onClick, invited label), then render the
 * decorated result back through `Component`.
 *
 * Config keys are opaque here — we read the values straight back via `Object.values`, so we don't
 * depend on `menuItemId` being present on every item variant, and insertion order (hence GSN's
 * visible order) is preserved.
 */
export function InvitedAppShortcutsSlot({
	Component,
	shortcutItems,
}: ShortcutsSlotProps): React.JSX.Element {
	const resolverResult = useContext(InvitedAppShortcutsResolverContext);

	const shortcutsConfig = useMemo(
		() =>
			shortcutItems.reduce<ShortcutsConfig>((acc, item, index) => {
				acc[String(index)] = item;
				return acc;
			}, {}),
		[shortcutItems],
	);

	// Defensive: the provider always supplies a resolver result on this path, but if it is ever
	// missing, fail open to the default (undecorated) shortcuts menu rather than crashing the nav.
	if (!resolverResult) {
		return <Component />;
	}

	return (
		<InvitedAppShortcutsContainer shortcutsConfig={shortcutsConfig} resolverResult={resolverResult}>
			{({ referrableShortcuts }) => (
				<Component shortcutItems={Object.values(referrableShortcuts)} />
			)}
		</InvitedAppShortcutsContainer>
	);
}

type PlatformNavigationComponentProps = {
	content?: ReactNode; //react compoenent to show for any given view
	hideGSN?: boolean; //boolean to tell us if the current view should hide the global & GAS section
	scrollTreeToCurrent?: () => void; //function that should be fired whenever the navigation is expanded to scroll to the current pageTree page in the tree once implemented remove optional type
	onExpand?: () => void;
	onCollapse?: () => void;
	onPeekStart?: () => void;
	onPeekEnd?: (args: { trigger: 'mouse-leave' | 'side-nav-expand' }) => void;
	sideNavContentRef?: React.Ref<HTMLDivElement | null>; //optional ref passed in to be attached to SideNavigation platform component
	bottomElem?: ReactNode; // optional element to be rendered at the bottom of the side navigation
};

export const PlatformNavigationComponent = ({
	content,
	onExpand,
	onCollapse,
	onPeekStart,
	onPeekEnd,
	sideNavContentRef,
	hideGSN,
	bottomElem,
}: PlatformNavigationComponentProps): React.JSX.Element => {
	const { accessStatus, cloudId } = useSessionData();
	const networkStorageParams = useNetworkStorageParams();
	const { shortcuts: globalAppShortcuts, createTeamModalComponent } = useGlobalAppShortcuts();
	const forYouConfig = useForYouConfig();
	const itemsConfig = useMenuItemsConfig();
	const { homepageUri } = useContext(SPAViewContext);
	const { isCompanyHubVisible } = useShortcutVisibilityOverrides();

	const isCompanyHubSetAsCustomHome =
		isCompanyHubVisible && homepageUri.includes(getCompanyHubSpaceKey());
	const isCustomHomeVisible: boolean =
		!isCompanyHubSetAsCustomHome && !['/spa.action', '', 'homepageUri'].includes(homepageUri);

	const invitedAppShortcutsExperiment = useResolveInvitedAppShortcuts();
	const isEligibleToSeeInvitedAppShortcuts = useMemo(
		() =>
			// we split the kill-switch gate and targeting gate to avoid the overrides precedence issue
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			fg('twcg-460-personalised-invite-notifications-m2-gate') &&
			invitedAppShortcutsExperiment.ineligibilityReason === null &&
			invitedAppShortcutsExperiment.invitedApps?.jira?.eligible === true,
		[invitedAppShortcutsExperiment],
	);

	const isInvitedAppShortcutsEnabled =
		isEligibleToSeeInvitedAppShortcuts &&
		expVal('twcg-460-personalised-invite-notifications-m2', 'isEnabled', false);

	// GSN reports its hydrated shortcuts via `onShortcutsMenuSettled` but does not cache the resolved
	// product links itself. We derive the invited-app product links from those settled shortcuts and
	// persist them to the cookie cache so the next page load can seed the shortcuts menu during SSR.
	const jiraShortcut: GlobalShortcutLinkItem = useJira('');
	const invitedAppShortcutsConfig: ShortcutItemsConfig = useMemo(
		() => ({ jira: jiraShortcut }),
		[jiraShortcut],
	);
	const { handleUpdateProductLinksFromSettledShortcuts } = useInvitedAppProductLinks({
		shortcutItemsConfig: invitedAppShortcutsConfig,
		getCachedProductLinks: invitedAppShortcutsExperiment.getCachedProductLinks,
		updateHydratedProductLinks: invitedAppShortcutsExperiment.updateHydratedProductLinks,
	});

	const onShortcutsMenuSettled = useCallback(
		({
			shortcuts: hydratedShortcuts,
			visibleMenuIds: _visibleMenuIds,
		}: {
			shortcuts: ShortcutsConfig;
			visibleMenuIds: string[];
		}) => {
			if (
				// Only update the cache if the experiment is ineligible, this is to avoid overwriting the cache when the experiment is eligible
				// A caveat is when the users lose access to the product, the cache will not be updated until expires
				invitedAppShortcutsExperiment.ineligibilityReason !== null
			) {
				invitedAppShortcutsExperiment.updateFromHydratedShortcuts({
					shortcuts: hydratedShortcuts,
					limitToOnlyApsKeys: ['JIRA_SOFTWARE'],
					// In Confluence, we don't respect the visibleMenuIds from the shortcuts menu
					visibleMenuIds: ['jiraShortcut'],
				});
				// Persist the resolved product links to the cookie cache. This does not re-render the
				// current page; the shortcuts menu picks them up on the next page load's SSR read.
				handleUpdateProductLinksFromSettledShortcuts(hydratedShortcuts);
			}
		},
		[invitedAppShortcutsExperiment, handleUpdateProductLinksFromSettledShortcuts],
	);

	// only render global app shortcuts if we meet these conditions
	const showGlobalAppShortcuts = hasGlobalAppShortcutsAccess(accessStatus);

	useEffect(() => {
		if (isEligibleToSeeInvitedAppShortcuts) {
			Object.hasOwn(globalAppShortcuts, 'jiraShortcut') &&
				invitedAppShortcutsExperiment.fireFeatureExposed({
					isEnabled: expVal('twcg-460-personalised-invite-notifications-m2', 'isEnabled', false),
				});
		}
	}, [isEligibleToSeeInvitedAppShortcuts, invitedAppShortcutsExperiment, globalAppShortcuts]);

	const gsnProps = {
		cloudId,
		forYouConfig,
		isCustomHomeVisible,
		itemsConfig,
		content,
		onExpand,
		onCollapse,
		onPeekStart,
		onPeekEnd,
		sideNavContentRef,
		globalAppShortcuts,
		onShortcutsMenuSettled,
		invitedAppShortcutsExperiment,
		isInvitedAppShortcutsEnabled,
		showGlobalAppShortcuts,
		networkStorageParams,
		hideGSN,
		bottomElem,
	};

	return (
		<>
			{/*
			 * Suspense catches useLazyLoadQuery suspensions from GlobalSideNavigationAGGRelayConnection
			 * on pages where no SSR AGG cache was injected (e.g. space settings pages, which are proxied
			 * to the monolith and don't go through Bifrost SSR). Without this boundary, the suspension
			 * would propagate upward — SuspenseWithDataFetcher only adds a Suspense wrapper when
			 * window.__SSR_REACT_STREAMING__ is true, so non-SSR pages have no boundary to catch it,
			 * causing React error 426 and a hard crash.
			 *
			 * Using null fallback is intentional: the GSN will load once the network request resolves.
			 * On SSR pages the cache is always populated so suspension never occurs in practice.
			 */}
			<Suspense fallback={null}>
				<GlobalSideNavigationAGGRelayConnection {...networkStorageParams}>
					{({ queryRef }) => <ConfluenceGSN queryRef={queryRef} {...gsnProps} />}
				</GlobalSideNavigationAGGRelayConnection>
			</Suspense>
			{createTeamModalComponent}
		</>
	);
};

type ConfluenceGSNProps = {
	queryRef: GlobalSideNavigationAGGRelayConnectionQuery$data;
	cloudId: string | undefined;
	forYouConfig: ReturnType<typeof useForYouConfig>;
	isCustomHomeVisible: boolean;
	itemsConfig: ReturnType<typeof useMenuItemsConfig>;
	content?: ReactNode;
	onExpand?: () => void;
	onCollapse?: () => void;
	onPeekStart?: () => void;
	onPeekEnd?: (args: { trigger: 'mouse-leave' | 'side-nav-expand' }) => void;
	hideGSN?: boolean;
	sideNavContentRef?: React.Ref<HTMLDivElement | null>;
	globalAppShortcuts: ReturnType<typeof useGlobalAppShortcuts>['shortcuts'];
	onShortcutsMenuSettled: (args: { shortcuts: ShortcutsConfig; visibleMenuIds: string[] }) => void;
	invitedAppShortcutsExperiment: ReturnType<typeof useResolveInvitedAppShortcuts>;
	isInvitedAppShortcutsEnabled: boolean;
	showGlobalAppShortcuts: boolean;
	networkStorageParams: ReturnType<typeof useNetworkStorageParams>;
	bottomElem?: ReactNode;
};

/**
 * Renders the GlobalSideNavigation with the appropriate props.
 * Extracted as a component (rather than a render function) so React can properly
 * manage its lifecycle and avoid unnecessary re-renders.
 */
function ConfluenceGSN({
	queryRef,
	cloudId,
	forYouConfig,
	isCustomHomeVisible,
	itemsConfig,
	content,
	onExpand,
	onCollapse,
	onPeekStart,
	onPeekEnd,
	sideNavContentRef,
	globalAppShortcuts, //todo: when hideGSN is true we dont' need to query for these
	onShortcutsMenuSettled,
	invitedAppShortcutsExperiment,
	isInvitedAppShortcutsEnabled,
	showGlobalAppShortcuts,
	networkStorageParams,
	hideGSN,
	bottomElem,
}: ConfluenceGSNProps): React.JSX.Element | null {
	// NetworkStorageParams includes queryRef typed as GlobalSideNavigationAGGRelayConnectionQuery$data.
	// The cast is needed because CI typechecks against pre-built declarations which may not have
	// resolved the Relay-generated type. Runtime behaviour is correct.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const networkStorageParamsWithQueryRef = { queryRef, ...networkStorageParams } as any;
	// Keep SSR and the first client render on the same branch (hydration Must rule #2): both
	// branches render GlobalSideNavigation with the EXACT same props — same `items`, `postItemsElem`
	// (the page tree), shortcuts-settled callback, etc. The ONLY difference is the `shortcuts`
	// source: referrableShortcuts (filtered by InvitedAppShortcutsContainer) vs globalAppShortcuts.
	// This way an SSR↔CSR flip of `isInvitedAppShortcutsEnabled` can no longer add/remove Spaces,
	// Apps, or the page tree and trigger a hydration mismatch.
	const sharedGlobalSideNavigationProps = {
		app: 'confluence' as const,
		customizationStore: 'network' as const,
		testId: 'global-side-navigation',
		appLabel: 'Confluence',
		cloudId,
		preMainElem: hideGSN ? null : isCustomHomeVisible && <CustomHomeItem />,
		forYou: hideGSN ? {} : forYouConfig,
		items: hideGSN ? {} : itemsConfig,
		disableMenuCustomization: hideGSN,
		postItemsElem: content,
		networkStorageParams: networkStorageParamsWithQueryRef,
		shortcuts: hideGSN ? undefined : globalAppShortcuts,
		bottomElem,
		onLeftNavExpand: onExpand,
		onLeftNavCollapse: onCollapse,
		onPeekStart,
		onPeekEnd,
		onResizeEnd: mirrorSideNavWidthToLocalStorage,
		sideNavContentRef,
		defaultMenuVisibility,
		...(fg('twcg-460-personalised-invite-notifications-m2-gate') && {
			experimental_onShortcutsMenuSettled: onShortcutsMenuSettled,
		}),
	};

	if (isInvitedAppShortcutsEnabled && showGlobalAppShortcuts) {
		// Render the invited-app shortcuts through the GSN `ShortcutsSlot` extension point (NAVX-5236)
		// rather than wrapping the whole nav and swapping its `shortcuts` config. The resolver result
		// is passed to the (module-level, stable-identity) slot component via context so GSN never
		// remounts the shortcuts subtree on a resolver-result change.
		return (
			<InvitedAppShortcutsResolverContext.Provider value={invitedAppShortcutsExperiment}>
				<GlobalSideNavigation
					{...sharedGlobalSideNavigationProps}
					ShortcutsSlot={hideGSN ? undefined : InvitedAppShortcutsSlot}
				/>
			</InvitedAppShortcutsResolverContext.Provider>
		);
	}
	return <GlobalSideNavigation {...sharedGlobalSideNavigationProps} />;
}
